import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronUp, ChevronDown, ExternalLink, Settings, BarChart3 } from 'lucide-react';

/**
 * AdminFloatingButton - Shows a floating button on public pages when admin is logged in
 * Provides quick access back to the admin dashboard
 */
export default function AdminFloatingButton() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      // Verify token is still valid (basic check)
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          const exp = payload.exp * 1000; // Convert to milliseconds
          if (exp > Date.now()) {
            setIsAdmin(true);
          }
        }
      } catch (e) {
        // Token invalid or expired
        setIsAdmin(false);
      }
    }
  }, []);

  if (!isAdmin) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50">
      {/* Expanded Menu */}
      {isExpanded && (
        <div className="mb-2 bg-white rounded-lg shadow-xl border border-blue-200 overflow-hidden animate-in slide-in-from-bottom-2">
          <div className="p-2 bg-blue-600 text-white text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Admin Quick Links
          </div>
          <div className="p-1">
            <Link 
              to="/admin/dashboard" 
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded transition-colors"
            >
              <BarChart3 className="h-4 w-4 text-blue-600" />
              Dashboard
            </Link>
            <Link 
              to="/admin/dashboard?tab=contest" 
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded transition-colors"
            >
              <Settings className="h-4 w-4 text-blue-600" />
              Contest Manager
            </Link>
            <a 
              href="/admin/dashboard" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 rounded transition-colors"
            >
              <ExternalLink className="h-4 w-4 text-blue-600" />
              Open in New Tab
            </a>
          </div>
        </div>
      )}
      
      {/* Main Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all hover:shadow-xl"
      >
        <Shield className="h-5 w-5" />
        <span className="font-medium">Admin</span>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronUp className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}
